import { useEffect, useState, useRef } from 'react'
import { Button } from '../../shared/components/Button'
import { FormFieldRenderer, generateEmptyValues } from '../../util/formFieldRenderer'
import { handleFormChange } from '../../util/handleFormChange'
import { FaPen } from 'react-icons/fa'
import { FaX } from 'react-icons/fa6'
import { API_URL } from '../../constants'
import { validateFields } from '../../util/validateField'
import { useNavigate } from 'react-router-dom'
import EmailPreview from './EmailPreview'
import { useTranslation } from 'react-i18next'
import { LoadingOverlay } from '../../shared/components/Loading'

export function EditableFormPage({
    fetchUrl,
    submitUrl = fetchUrl || '',
    fields = [],
    token = localStorage.getItem('token'),
    classNameForField = field => '', // optional custom grid className
    loadingMessage,
    errorMessage = 'Something went wrong.',
    language = 'en',
    create = false,
    readOnly = false,
    options,
    navigateLink = '/admin/auth/platform-information',
    acceptJSON = false,
    method = 'POST',
    previewEmail = false,
    title = '',
}) {
    const [form, setForm] = useState(generateEmptyValues(fields) || {})
    const [initialForm, setInitialForm] = useState({})
    const [errors, setErrors] = useState({})
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const [edit, setEdit] = useState(false)
    const formRef = useRef({}) // To track initial form values

    const { t } = useTranslation()
    const navigate = useNavigate()

    const handleChange = handleFormChange(setForm)

    const fetchData = async () => {
        setLoading(true)
        setErrorMsg('')
        try {
            const response = await fetch(`${API_URL}/${fetchUrl}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Accept-Language': language,
                },
            })
            const result = await response.json()
            if (response.ok) {
                setForm(result.data)
                setInitialForm(result.data) // Store initial values
                formRef.current = result.data // Store in ref for comparison
            } else {
                setErrorMsg(result.message || errorMessage)
            }
        } catch (error) {
            setErrorMsg('Network error. Please try again.')
            console.error('Fetch error:', error)
        } finally {
            setLoading(false)
        }
    }

    // Helper function to check if a field is an image/file field
    const isImageField = fieldName => {
        const fieldDef = fields.find(f => f.name === fieldName)
        return fieldDef?.type === 'file' || fieldDef?.type === 'upload'
    }

    // Helper function to compare two values deeply
    const isValueChanged = (newValue, oldValue, fieldType) => {
        if (newValue === undefined && oldValue === undefined) return false
        if (newValue === null && oldValue === null) return false
        if (newValue === '' && oldValue === null) return false
        if (newValue === null && oldValue === '') return false

        // For arrays (checkbox-group, etc.)
        if (Array.isArray(newValue) && Array.isArray(oldValue)) {
            if (newValue.length !== oldValue.length) return true
            return newValue.some((val, index) => val !== oldValue[index])
        }

        // For objects
        if (typeof newValue === 'object' && typeof oldValue === 'object' && newValue && oldValue) {
            const newKeys = Object.keys(newValue)
            const oldKeys = Object.keys(oldValue)
            if (newKeys.length !== oldKeys.length) return true
            return newKeys.some(key => newValue[key] !== oldValue[key])
        }

        // Simple comparison for primitives
        return newValue !== oldValue
    }

    // Helper function to check if image field has actually changed
    const hasImageChanged = (currentValue, initialValue) => {
        // If it's a File object (new upload), it's definitely changed
        if (currentValue instanceof File) return true

        // If one is null/empty and the other isn't, it's changed
        if (
            (currentValue === null || currentValue === '') &&
            initialValue !== null &&
            initialValue !== ''
        )
            return true
        if (
            (initialValue === null || initialValue === '') &&
            currentValue !== null &&
            currentValue !== ''
        )
            return true

        // If both are strings (URLs), compare them
        if (typeof currentValue === 'string' && typeof initialValue === 'string') {
            return currentValue !== initialValue
        }

        // For other cases, assume no change
        return false
    }

    // Get fields to submit: non-image fields always, image fields only if changed
    const getFieldsToSubmit = (currentForm, initialFormRef) => {
        const fieldsToSubmit = {}

        for (const key in currentForm) {
            const currentValue = currentForm[key]
            const initialValue = initialFormRef.current[key]
            const fieldDef = fields.find(f => f.name === key)

            if (isImageField(key)) {
                // Image fields: only submit if changed
                if (hasImageChanged(currentValue, initialValue)) {
                    fieldsToSubmit[key] = currentValue
                }
                // If not changed, don't include it
            } else {
                // Non-image fields: always submit, use current value
                // If current value is undefined/null, use initial value
                const valueToSubmit = currentValue !== undefined ? currentValue : initialValue
                if (valueToSubmit !== undefined) {
                    fieldsToSubmit[key] = valueToSubmit
                }
            }
        }

        return fieldsToSubmit
    }

    const handleSubmit = async () => {
        const isValid = validateFields(fields, form, setErrors, t)
        if (!isValid) return

        setSubmitting(true)
        setErrors({})
        setErrorMsg('')

        try {
            let body
            let headers = {
                Authorization: `Bearer ${token}`,
                'Accept-Language': language,
            }

            // Get fields to submit: non-image always, images only if changed
            const dataToSend = edit && !create ? getFieldsToSubmit(form, formRef) : form

            // Check if there are any changes at all
            const hasChanges = Object.keys(dataToSend).length > 0

            // If no fields to submit (all fields unchanged, including images), show message
            if (edit && !create && !hasChanges) {
                setErrorMsg(t('status.no_changes'))
                setSubmitting(false)
                return
            }

            if (acceptJSON) {
                // Prepare JSON payload
                const payload = {}

                for (const key in dataToSend) {
                    let value = dataToSend[key]
                    if (value !== null && value !== undefined) {
                        const fieldDef = fields.find(f => f.name === key)

                        // Handle special field types
                        if (fieldDef?.type === 'checkbox-group') {
                            if (typeof value === 'string') value = value.split(',')
                        }

                        // For image fields with File objects, we can't send them in JSON
                        if (isImageField(key) && value instanceof File) {
                            console.warn(
                                `Image field ${key} has File object - consider using FormData instead of JSON`
                            )
                            // You might want to skip or handle this differently
                            continue
                        }

                        payload[key] = value
                    }
                }

                body = JSON.stringify(payload)
                headers['Content-Type'] = 'application/json'
            } else {
                // Prepare FormData payload (better for files)
                body = new FormData()

                for (const key in dataToSend) {
                    let value = dataToSend[key]
                    if (value !== null && value !== undefined) {
                        const fieldDef = fields.find(f => f.name === key)

                        // Handle special field types
                        if (fieldDef?.type === 'checkbox-group') {
                            if (typeof value === 'string') value = value.split(',')
                        }

                        if (fieldDef?.type === 'group' && Array.isArray(value)) {
                            value.forEach((groupItem, index) => {
                                for (const subKey in groupItem) {
                                    body.append(`${key}[${index}][${subKey}]`, groupItem[subKey])
                                }
                            })
                        } else if (fieldDef?.type === 'checkbox-group' && Array.isArray(value)) {
                            value.forEach((v, index) => {
                                body.append(`${key}[${index}]`, v)
                            })
                        } else if (isImageField(key)) {
                            // Special handling for image fields
                            if (value instanceof File) {
                                // It's a new file upload
                                body.append(key, value)
                            } else if (value === '' || value === null) {
                                // Image was cleared - send empty
                                body.append(key, '')
                            } else if (typeof value === 'string') {
                                // It's a string URL of existing image
                                // Many APIs expect to receive the image URL to update
                                body.append(key, value)
                            }
                        } else {
                            // Non-image fields - always include
                            const normalizedValue =
                                typeof value === 'boolean' ? (value ? 1 : 0) : value
                            body.append(key, normalizedValue)
                        }
                    }
                }
            }

            const response = await fetch(`${API_URL}/${submitUrl}`, {
                method: method,
                headers,
                body,
            })

            const result = await response.json()

            if (!response.ok) {
                setErrorMsg(result.message || errorMessage)
                if (result.errors) setErrors(result.errors)
            } else {
                // Update initial form reference after successful submission
                if (edit && !create) {
                    // Update only the fields that were submitted
                    const updatedInitial = { ...formRef.current }
                    for (const key in dataToSend) {
                        updatedInitial[key] = form[key]
                    }
                    formRef.current = updatedInitial
                    setInitialForm(updatedInitial)
                }
                setEdit(false)
                if (create) {
                    navigate(navigateLink)
                } else {
                    fetchData() // Refresh data from server to get updated image URLs
                }
            }
        } catch (error) {
            setErrorMsg('Network error. Please try again.')
            console.error('Submit error:', error)
        } finally {
            setSubmitting(false)
        }
    }

    // Reset form when edit mode is cancelled
    useEffect(() => {
        if (!edit && !create && initialForm) {
            setForm(initialForm)
        }
    }, [edit, create, initialForm])

    useEffect(() => {
        if (fetchUrl) {
            fetchData()
        }
        // eslint-disable-next-line
    }, [])

    return (
        <div className='relative w-full p-6 bg-white border shadow-md rounded-xl'>
            {submitting && <LoadingOverlay message={t('submitting')} />}

            {/* Sticky Error Message */}
            {errorMsg && (
                <div className='fixed top-0 left-0 right-0 z-50 max-w-xl p-4 mx-6 mt-6 text-sm text-red-600 transition-transform duration-200 ease-out transform border border-red-200 rounded shadow-lg bg-red-50'>
                    <div className='flex items-center justify-between'>
                        <span>{errorMsg}</span>
                        <button
                            onClick={() => setErrorMsg('')}
                            className='ml-4 text-lg font-semibold text-red-800 hover:text-red-900 focus:outline-none'
                            aria-label='Dismiss error'
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            <div className='flex justify-between md:mb-12'>
                <h2 className='text-2xl font-bold text-gray-800'>
                    {title
                        ? title
                        : create
                          ? t('status.create')
                          : edit
                            ? t('status.edit')
                            : t('status.details')}
                </h2>
                {!create && !readOnly && (
                    <Button variant='secondary' size='small' onClick={() => setEdit(!edit)}>
                        {edit ? <FaX className='mr-2' /> : <FaPen className='mr-2' />}
                        {edit ? t('status.cancel_edit') : t('status.edit')}
                    </Button>
                )}
            </div>

            {loading ? (
                <LoadingOverlay message={loading || t('loading')} />
            ) : // <p className='text-gray-500'>{loadingMessage || t('loading')}</p>
            previewEmail ? (
                <EmailPreview form={form} />
            ) : (
                <>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                        {fields.map(field => (
                            <div key={field.name} className={classNameForField(field)}>
                                <FormFieldRenderer
                                    key={field.name}
                                    value={form[field.name]}
                                    field={field}
                                    onChange={handleChange}
                                    error={errors[field.name]}
                                    readOnly={create === false && !edit}
                                    options={options}
                                    allValues={form}
                                />
                            </div>
                        ))}
                    </div>

                    {(edit || create === true) && (
                        <div className='flex items-end justify-end w-full mt-8'>
                            <Button
                                variant='primary'
                                size='large'
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting
                                    ? t('status.saving')
                                    : create
                                      ? t('status.submit')
                                      : t('status.save_changes')}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
